import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantService } from '@app/Services';
import { FormFlowService } from '@app/Services/formFlow.service';
import { Observable, Subject } from 'rxjs';
import { map, mergeMap, takeUntil } from 'rxjs/operators';
import { AutoFillDefinitionBuilder } from '../../builders/auto-fill-definition.builder';
import { AutoFillDefinition, Field, ModuleContent, Step } from '../../Models';
import { ProcessType } from '../../Models/configuration-options/process-type';
import { IAutoFillFieldMapping } from '../../Models/ModuleDefinitions/auto-fill-field-mapping';
import { BasicField } from '../../Models/ModuleDefinitions/basic-field';
import { ModuleService } from '../../Services';
import { SourceType } from '../../Models/configuration-options/source-type';
import { MatToolbar } from '@angular/material/toolbar';
import { ProcessTypeConfigOptionComponent } from '../configuration-options/process-type/process-type.component';
import { AutofillFieldMappingComponent } from '../autofill-field-mapping/autofill-field-mapping.component';
import { MatCard, MatCardActions } from '@angular/material/card';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'linked-record-configuration',
    templateUrl: 'linked-record-configuration.component.html',
    styleUrls: ['linked-record-configuration.component.less'],
    imports: [MatToolbar, ProcessTypeConfigOptionComponent, AutofillFieldMappingComponent, MatCard, MatCardActions, MatButton]
})
export class LinkedRecordConfigurationComponent implements OnInit, OnDestroy {
	linkedRecordModuleId: string;
	moduleId: string;
	stepId: string;
	version: number;
	getMultiFields: boolean;
	fieldId: string;
	field: Field;
	autoFillDefinition!: AutoFillDefinition;

	module: ModuleContent;
	autoFillFieldMappings: IAutoFillFieldMapping[] = [];
	currentStepFields: Field[] = [];
	fieldsForLinkedModule: BasicField[];

	loaded = false;
	saveEnabled = false;
	processType: ProcessType;

	private mappingsInitialzed = false;
	private destroyed$: Subject<void> = new Subject<void>();


	constructor(
		private activatedRoute: ActivatedRoute,
		private formFlowService: FormFlowService,
		private tenantService: TenantService,
		private moduleService: ModuleService,
		private router: Router,
		public snackBar: MatSnackBar
	) {}

	ngOnInit() {
		const queryParameters = this.activatedRoute.snapshot.queryParams;
		const routeParameters = this.activatedRoute.snapshot.params;

		this.moduleId = routeParameters.moduleId;
		this.stepId = queryParameters.stepId;
		this.version = +routeParameters.version;
		this.getMultiFields = queryParameters.getMultiFields === 'true';
		this.linkedRecordModuleId = queryParameters.linkedRecordModuleId;
		this.fieldId = routeParameters.fieldId;

		this.getCurrentStepFields();
		this.getFieldsForLinkedModule()
		.pipe(takeUntil(this.destroyed$))
		.subscribe((data => this.fieldsForLinkedModule = data));
	}

	ngOnDestroy(): void {
		this.destroyed$.next();
	}

	mappingsChanged($event: IAutoFillFieldMapping[]) {
		this.autoFillFieldMappings = $event;
		if (this.mappingsInitialzed) {
			this.saveEnabled = true;
		}
		this.mappingsInitialzed = true;
	}

	processTypeChanged($event: ProcessType) {
		this.processType = $event;
		this.saveEnabled = true;
	}

	goBack(): void {
		const queryParameters = this.activatedRoute.snapshot.queryParams;
		this.router.navigate(
			['../../../steps/step/fields-container/parent-fields'],
			{
				relativeTo: this.activatedRoute,
				queryParams: {
					stepId: queryParameters.stepId,
					getMultiFields: queryParameters.getMultiFields,
				},
			}
		);
	}

	onSave(): void {
		this.field.AutoFillDefinition = new AutoFillDefinitionBuilder()
			.setProcessingType(this.processType)
			.addControlValueMappings(this.autoFillFieldMappings, this.fieldsForLinkedModule, SourceType.LinkedRecord, this.processType)
			.addValidationHandlingOptions(
				false,
				false,
				true)
			.build();

		if (!this.field.IsCustomField) {
			this.field.Modified = true;
		}

		this.moduleService
			.patchField(
				this.tenantService.selectedTenant,
				this.moduleId,
				this.stepId,
				this.field
			)
			.pipe(
				mergeMap((patchResponse) =>
					this.moduleService.updateField(
						this.field,
						this.tenantService.selectedTenant,
						this.moduleId,
						this.version,
						this.stepId
					)
				)
			)
			.subscribe({
				next: () => {
					this.showSnackBarMessage('Configuration Saved');
					this.saveEnabled = false;
				},
				error: (error) => this.showErrorSavingSnackBar(error),
			});
	}

	private showErrorSavingSnackBar(error: any): void {
		console.error(error);
		this.showSnackBarMessage('Error Saving', true);
	}

	private showSnackBarMessage(message: string, error: boolean = false) {
		const config = new MatSnackBarConfig();
		config.duration = 10000;
		config.politeness = error ? 'assertive' : 'polite';
		this.snackBar.open(message, null, config);
	}

	private getFieldsForLinkedModule(): Observable<BasicField[]> {
		return this.formFlowService
			.getAllFieldsForModuleLatestVersion(
				this.tenantService.selectedTenant.TenantId,
				this.linkedRecordModuleId
			)
			.pipe(
				map((fields) =>
					fields.map((field) => ({
						name: field.ParentPropertyName,
						dataType: field.FieldType,
						mappings: field.Mappings.map((mapping) => ({
							modelPropertyName: mapping.ModelPropertyName,
							controlPropertyName: mapping.ControlPropertyName
						}))
					}))
				)
			);
	}

	private getCurrentStepFields() {
		this.moduleService
			.loadModuleContent(
				this.tenantService.selectedTenant,
				this.moduleId,
				this.version
			)
			.then((module) => {
				this.module = module;
				let fields: Field[];

				const step: Step = this.module.Steps.find(
					(x) => x.StepId === this.stepId
				);

				fields = this.getMultiFields
					? (fields = step.Fields.find(
							(y) => y.IsMulti === true
					  ).MultiFields)
					: step.Fields;

				this.field = fields.find(
					(field) => field.FieldId === this.fieldId
				);
				this.autoFillDefinition = this.field.AutoFillDefinition;
				this.processType = this.autoFillDefinition?.ProcessType ?? ProcessType.client;
				this.currentStepFields = fields.filter(
					(y) => y.FieldId !== this.fieldId
				);

				this.loaded = true;
			});
	}
}
